import React, { useState } from 'react';
import { Sparkles, RefreshCw, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../../config';
import { supabase } from '../../config/supabaseClient';

interface EnrichmentHelperProps {
  entityType: 'contact' | 'company';
  entityId: string;
  organizationId: string;
  currentData?: {
    email?: string;
    domain?: string;
    linkedin_url?: string;
    phone?: string;
  };
  enrichmentStatus?: {
    confidence?: number;
    lastEnriched?: string;
    sources?: string[];
  };
  onEnrichmentComplete?: () => void;
}

const EnrichmentHelper: React.FC<EnrichmentHelperProps> = ({
  entityType,
  entityId,
  organizationId,
  currentData,
  enrichmentStatus,
  onEnrichmentComplete
}) => {
  const [enriching, setEnriching] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const canEnrich = entityType === 'contact' 
    ? currentData?.email || currentData?.linkedin_url
    : currentData?.domain || currentData?.linkedin_url;

  const enrichmentQuality = enrichmentStatus?.confidence 
    ? enrichmentStatus.confidence > 70 ? 'good' 
      : enrichmentStatus.confidence > 40 ? 'medium' 
      : 'low'
    : 'none';

  const handleEnrich = async () => {
    console.log('[EnrichmentHelper] Attempting enrichment:', {
      entityType,
      entityId,
      organizationId,
      currentData,
      canEnrich
    });

    if (!canEnrich) {
      const missing = entityType === 'contact' 
        ? 'Email or LinkedIn URL' 
        : 'Domain or LinkedIn URL';
      console.error('[EnrichmentHelper] Cannot enrich - missing required data:', missing);
      toast.error(`${missing} required for enrichment`);
      return;
    }

    setEnriching(true);
    try {
      // Get auth token and user
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const userId = session?.user?.id;
      
      console.log('[EnrichmentHelper] Auth check:', { hasToken: !!token, hasUserId: !!userId });
      
      if (!token || !userId) {
        toast.error('Not authenticated');
        setEnriching(false);
        return;
      }

      // Call enrichment API - trim and remove trailing slash to prevent URL formatting issues
      const baseUrl = (API_BASE_URL || 'http://localhost:5000').trim().replace(/\/$/, '');
      const url = `${baseUrl}/api/crm/enrich/${entityType}/${entityId}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          userId,
          organization_id: organizationId,
          ...currentData
        })
      });

      console.log('[EnrichmentHelper] Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[EnrichmentHelper] Enrichment failed:', errorData);
        throw new Error(errorData.error || 'Enrichment failed');
      }

      const result = await response.json();
      console.log('[EnrichmentHelper] Enrichment successful:', result);
      toast.success(`${entityType === 'contact' ? 'Contact' : 'Company'} enriched successfully!`);
      onEnrichmentComplete?.();
    } catch (error) {
      console.error('[EnrichmentHelper] Enrichment error:', error);
      toast.error(error instanceof Error ? error.message : 'Enrichment failed. Please try again.');
    } finally {
      console.log('[EnrichmentHelper] Enrichment complete, resetting loading state');
      setEnriching(false);
    }
  };

  const getQualityBadge = () => {
    switch (enrichmentQuality) {
      case 'good':
        return (
          <div className="flex items-center gap-1.5 text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-xs font-medium">High quality</span>
          </div>
        );
      case 'medium':
        return (
          <div className="flex items-center gap-1.5 text-yellow-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Medium quality</span>
          </div>
        );
      case 'low':
        return (
          <div className="flex items-center gap-1.5 text-orange-700">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Low quality</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 text-gray-500">
            <AlertCircle className="h-4 w-4" />
            <span className="text-xs font-medium">Not enriched</span>
          </div>
        );
    }
  };

  const getLastEnrichedText = () => {
    if (!enrichmentStatus?.lastEnriched) return 'Never';
    
    const date = new Date(enrichmentStatus.lastEnriched);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <h3 className="text-sm font-semibold text-gray-900">AI Enrichment</h3>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-gray-400 transition hover:text-gray-600"
          >
            <Info className="h-4 w-4" />
          </button>
        </div>
        {getQualityBadge()}
      </div>

      {showInfo && (
        <div className="mb-3 rounded-lg bg-white/60 p-3 text-xs text-gray-600">
          <p className="mb-2 font-medium text-gray-800">What is enrichment?</p>
          <p className="mb-2">
            AI enrichment automatically finds and adds missing information like job titles, 
            company size, industry, technologies used, and social profiles.
          </p>
          <p className="font-medium text-gray-800">Data sources:</p>
          <ul className="ml-4 mt-1 list-disc space-y-1">
            <li>LinkedIn profiles</li>
            <li>Company websites</li>
            <li>Public databases</li>
            <li>Social media</li>
          </ul>
        </div>
      )}

      <div className="mb-3 grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg bg-white/60 p-2">
          <div className="text-gray-600">Confidence</div>
          <div className="mt-1 font-semibold text-gray-900">
            {enrichmentStatus?.confidence ? `${Math.round(enrichmentStatus.confidence)}%` : '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white/60 p-2">
          <div className="text-gray-600">Last enriched</div>
          <div className="mt-1 font-semibold text-gray-900">
            {getLastEnrichedText()}
          </div>
        </div>
      </div>

      {enrichmentStatus?.sources && enrichmentStatus.sources.length > 0 && (
        <div className="mb-3">
          <div className="mb-1.5 text-xs font-medium text-gray-700">Sources</div>
          <div className="flex flex-wrap gap-1.5">
            {enrichmentStatus.sources.map((source) => (
              <span
                key={source}
                className="rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-medium text-gray-700"
              >
                {source}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={handleEnrich}
        disabled={enriching || !canEnrich}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:from-purple-700 hover:to-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {enriching ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            Enriching...
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            {enrichmentQuality === 'none' ? 'Enrich Now' : 'Re-enrich'}
          </>
        )}
      </button>

      {!canEnrich && (
        <p className="mt-2 text-center text-xs text-gray-500">
          {entityType === 'contact' 
            ? 'Add email or LinkedIn URL to enable enrichment'
            : 'Add domain or LinkedIn URL to enable enrichment'}
        </p>
      )}
    </div>
  );
};

export default EnrichmentHelper;
